/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { CssTools } from './CssTools.js';
/**
 * A color is either
 * * a string that denotes a CSS color value (see https://developer.mozilla.org/en-US/docs/Web/CSS/color_value) or
 * * a string that denotes a CSS variable which in turn can be resolved to a CSS color value or
 * * a number >= 0 that denotes an index into a Palette
 */

/**
 * Guard for type 'Color'
 *
 * @param color - a potential instance of Color
 * @returns a type predicate for Color
 */
export function isColor(color) {
  return typeof color === 'string' || typeof color === 'number' && color >= 0 && color !== Number.POSITIVE_INFINITY;
}
/**
 * Contains all colors of the namur palette, identified by 'namur'
 */

export const namurPaletteColors = ['var(--davinci-color-palette-namur-01)', 'var(--davinci-color-palette-namur-02)', 'var(--davinci-color-palette-namur-03)', 'var(--davinci-color-palette-namur-04)', 'var(--davinci-color-palette-namur-05)'];
/**
 * Contains all colors of the palette that is optimized for color-blind people, identified by 'color-blind'
 */

export const colorBlindPaletteColors = ['var(--davinci-color-palette-colorblind-01)', 'var(--davinci-color-palette-colorblind-02)', 'var(--davinci-color-palette-colorblind-03)', 'var(--davinci-color-palette-colorblind-04)', 'var(--davinci-color-palette-colorblind-05)'];
/**
 * Contains all colors of the primary palette, identified by 'primary'
 */

export const primaryPaletteColors = ['var(--davinci-color-palette-primary-01)', 'var(--davinci-color-palette-primary-02)', 'var(--davinci-color-palette-primary-03)', 'var(--davinci-color-palette-primary-04)', 'var(--davinci-color-palette-primary-05)', 'var(--davinci-color-palette-primary-06)', 'var(--davinci-color-palette-primary-07)', 'var(--davinci-color-palette-primary-08)', 'var(--davinci-color-palette-primary-09)', 'var(--davinci-color-palette-primary-10)', 'var(--davinci-color-palette-primary-11)', 'var(--davinci-color-palette-primary-12)'];
/**
 * Contains all colors of the secondary palette, identified by 'secondary'
 */

export const secondaryPaletteColors = ['var(--davinci-color-palette-secondary-01)', 'var(--davinci-color-palette-secondary-02)', 'var(--davinci-color-palette-secondary-03)', 'var(--davinci-color-palette-secondary-04)', 'var(--davinci-color-palette-secondary-05)', 'var(--davinci-color-palette-secondary-06)', 'var(--davinci-color-palette-secondary-07)', 'var(--davinci-color-palette-secondary-08)', 'var(--davinci-color-palette-secondary-09)', 'var(--davinci-color-palette-secondary-10)', 'var(--davinci-color-palette-secondary-11)', 'var(--davinci-color-palette-secondary-12)'];
/**
 * Contains all colors of the state palette, identified by 'state'
 */

export const statePaletteColors = ['var(--davinci-color-palette-state-01)', 'var(--davinci-color-palette-state-02)', 'var(--davinci-color-palette-state-03)', 'var(--davinci-color-palette-state-04)'];
/**
 * A palette is an non-empty array of CSS colors or a string identifying an array of CSS colors
 */

/**
 * Retrieve a list of all available palette identifiers.
 */
export function getPaletteIdentifiers() {
  return ['primary', 'secondary', 'state', 'color-blind', 'namur'];
}
/**
 * Guard for type 'Palette'
 *
 * @param palette - a potential instance of Palette
 * @returns a type predicate for Palette
 */

export function isPalette(palette) {
  if (Array.isArray(palette)) {
    if (palette.length < 1) {
      return false;
    }

    return palette.every(color => typeof color === 'string');
  }

  return isPaletteIdentifier(palette);
}
/**
 * Determines whether the given argument is a valid identifier of a palette.
 *
 * @param identifier - the identifier to be checked.
 * @returns true if the identifier identifies a palette else false
 */

export function isPaletteIdentifier(identifier) {
  if (typeof identifier !== 'string') {
    return false;
  }

  switch (identifier) {
    case 'primary':
    case 'secondary':
    case 'state':
    case 'color-blind':
    case 'namur':
      return true;

    default:
      return false;
  }
}
/**
 * Converts the given palette into an array of CSS colors.
 *
 * @param palette - the palette to be converted
 * @returns an array of CSS colors; be aware that these may be CSS variables
 */

export function toCssColors(palette) {
  if (typeof palette === 'string') {
    switch (palette) {
      case 'primary':
        return primaryPaletteColors;

      case 'secondary':
        return secondaryPaletteColors;

      case 'state':
        return statePaletteColors;

      case 'color-blind':
        return colorBlindPaletteColors;

      case 'namur':
        return namurPaletteColors;

      default:
        throw new Error(`unknown palette <${palette}>`);
    }
  }

  return palette;
}
/**
 * Converts the given color into a CSS color value
 *
 * @param color - the color to be converted
 * @param palette - the palette to be used in case 'color' denotes an index
 * @returns a CSS color value
 */

export function toCssColorValue(color, palette) {
  if (typeof color === 'string') {
    return resolveCssColor(color);
  }

  const cssColors = toCssColors(palette);
  const index = color % cssColors.length;
  return resolveCssColor(cssColors[index]);
}
/**
 * Converts the given CSS color into a CSS color value
 *
 * @param color - a CSS color value or a CSS variable referencing a CSS color value
 * @returns a CSS color value
 */

export function resolveCssColor(color) {
  if (color.startsWith('var')) {
    return ColorVariableResolver.RESOLVE_COLOR_VARIABLE(color);
  }

  return color;
}
/**
 * Helper class to resolve CSS variables referencing CSS color values
 */

class ColorVariableResolver {
  static _getOrCreateResolverElement() {
    const id = 'davinciCssResolverElement';
    let resolverElement = document.getElementById(id);

    if (resolverElement) {
      return resolverElement;
    }

    resolverElement = document.createElement('DIV');
    resolverElement.setAttribute('id', id);
    resolverElement.style.width = '0';
    resolverElement.style.height = '0';
    resolverElement.style.display = 'none';
    document.body.appendChild(resolverElement);
    return resolverElement;
  }
  /**
   * Resolves the given CSS variable into an CSS color value
   *
   * @param colorVariable - the CSS variable to be resolved
   * @returns the CSS color value referenced by the given CSS variable
   */


  static RESOLVE_COLOR_VARIABLE(colorVariable) {
    const htmlElement = this._getOrCreateResolverElement();

    htmlElement.style.color = colorVariable;

    if (htmlElement.style.color.length === 0) {
      // probably IE11 which does not support CSS variables
      return CssTools.GET_FALLBACK_VALUE_FROM_VARIABLE(colorVariable);
    }

    return CssTools.GET_COMPUTED_COLOR(htmlElement) || CssTools.GET_FALLBACK_VALUE_FROM_VARIABLE(colorVariable);
  }

}
